"use client";

import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import type { DietAddonGroupDietAddonsRead } from "@repo/api/catalog/api/schemas/diet-addon-group-diet-addons-read";
import { useMemo } from "react";
import { useAddonViewItemListEvent } from "@/features/analytics/hooks/use-addon-view-item-list-event";
import { AddonCategoryTabs } from "./addon-category-tabs";
import { DietAddonCard } from "./diet-addon-card";

const byPosition = <T extends { position: number }>(a: T, b: T) =>
  a.position - b.position;

type DietAddonsAvailableListProps = {
  categories: DietAddonGroupDietAddonsRead[];
  activeCategory: string;
  onSelectCategory: (slug: string) => void;
  onAddAddon: (addon: AddonDietAddonsRead) => void;
  daysCount?: number;
  // Desktop renders its own category tab row; on mobile the tab bar lives at the
  // dialog level (alongside the "Twoje dodatki" tab), so it is hidden here.
  showTabs: boolean;
  cateringName?: string;
  isVisible?: boolean;
};

// Left column (desktop) / category panel (mobile): category tabs filter the grid
// of available addon cards, matching the section style from MPL-3722.
export const DietAddonsAvailableList = ({
  categories,
  activeCategory,
  onSelectCategory,
  onAddAddon,
  daysCount,
  showTabs,
  cateringName,
  isVisible = true,
}: DietAddonsAvailableListProps) => {
  const sortedCategories = useMemo(
    () => [...categories].sort(byPosition),
    [categories],
  );

  const active = useMemo(
    () =>
      sortedCategories.find((category) => category.slug === activeCategory) ??
      sortedCategories.at(0),
    [sortedCategories, activeCategory],
  );

  const activeAddons = useMemo(
    () =>
      (active?.addons ?? []).filter((addon) => addon.active).sort(byPosition),
    [active],
  );

  useAddonViewItemListEvent({
    addons: activeAddons,
    cateringName,
    categoryName: active?.name,
    enabled: isVisible,
    listType: "addon-modal",
  });

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6">
      {showTabs && (
        <AddonCategoryTabs
          activeCategory={activeCategory}
          categories={sortedCategories}
          className="shrink-0"
          onSelectCategory={onSelectCategory}
        />
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {active && (
          <section className="space-y-4">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {activeAddons.map((addon, position) => (
                <DietAddonCard
                  addon={addon}
                  cateringName={cateringName}
                  categoryName={active?.name}
                  index={position + 1}
                  key={addon.id}
                  listType="addon-modal"
                  onAddAddonAction={onAddAddon}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
